import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);
const AUTH_REFRESH_MS = 60000;
const SILENT_AUTH_MIN_MS = 30000;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = unknown, false = anon, obj = authed
  const [loading, setLoading] = useState(true);
  const silentRefreshRef = useRef({ last: 0, running: false });

  const fetchMe = useCallback(async (options = {}) => {
    const silent = Boolean(options?.silent);
    const adminContext = typeof window !== "undefined" && window.location.pathname.startsWith("/admin");
    const token = adminContext
      ? localStorage.getItem("aurum_admin_token") || localStorage.getItem("aurum_token")
      : localStorage.getItem("aurum_token");
    if (!token) {
      setUser(false);
      if (!silent) setLoading(false);
      return null;
    }
    try {
      const { data } = await api.get("/auth/me", {
        timeout: 12000,
        ...(silent ? { skipMaintenance: true } : {}),
      });
      if (!adminContext && ["admin", "staff"].includes(data?.role)) {
        localStorage.removeItem("aurum_token");
        setUser(false);
        return null;
      }
      setUser(data);
      return data;
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403 || !silent) {
        if (adminContext) localStorage.removeItem("aurum_admin_token");
        else localStorage.removeItem("aurum_token");
        setUser(false);
      }
      return null;
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchMe(); }, [fetchMe]);

  useEffect(() => {
    const refreshIfVisible = () => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
      const now = Date.now();
      if (silentRefreshRef.current.running || now - silentRefreshRef.current.last < SILENT_AUTH_MIN_MS) return;
      silentRefreshRef.current.last = now;
      silentRefreshRef.current.running = true;
      Promise.resolve(fetchMe({ silent: true })).finally(() => {
        silentRefreshRef.current.running = false;
      });
    };
    const interval = window.setInterval(refreshIfVisible, AUTH_REFRESH_MS);
    window.addEventListener("focus", refreshIfVisible);
    window.addEventListener("pageshow", refreshIfVisible);
    window.addEventListener("online", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener("focus", refreshIfVisible);
      window.removeEventListener("pageshow", refreshIfVisible);
      window.removeEventListener("online", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, [fetchMe]);

  useEffect(() => {
    const expire = () => {
      localStorage.removeItem("aurum_token");
      setUser(false);
    };
    window.addEventListener("sophia:auth-expired", expire);
    return () => window.removeEventListener("sophia:auth-expired", expire);
  }, []);

  const login = (token, userObj) => {
    if (["admin", "staff"].includes(userObj?.role)) {
      localStorage.setItem("aurum_admin_token", token);
      localStorage.removeItem("aurum_token");
    } else {
      localStorage.setItem("aurum_token", token);
    }
    setUser(userObj);
  };

  const logout = () => {
    if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
      localStorage.removeItem("aurum_admin_token");
    }
    localStorage.removeItem("aurum_token");
    setUser(false);
  };

  const refreshUser = fetchMe;

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
